
import {
  type Accessor,
} from "solid-js";
import { Project } from "@mux/sdk/v2";
import { type SessionScroll } from "./layout-scroll";
import { createPathHelpers, isOutsideWorkspacePath } from "./file/path";

export const AVATAR_COLOR_KEYS = [
  "pink",
  "mint",
  "orange",
  "purple",
  "cyan",
  "lime",
] as const;

export const DEFAULT_PANEL_WIDTH = 344;

export type RightSidebarTab = "explorer" | "changes" | "context";

/** The machine desktop panel's default width. */
export const DESKTOP_PANEL_WIDTH = 640;

/** The narrowest width the machine desktop panel can be dragged to. */
export const DESKTOP_PANEL_MIN_WIDTH = 320;

/** The navigation sidebar's narrowest width, and the space it always leaves the page beside it. */
export const NAV_SIDEBAR_MIN_WIDTH = 110;
export const NAV_SIDEBAR_PAGE_MIN_WIDTH = 360;

export const DEFAULT_SIDEBAR_WIDTH = 260;

export const DEFAULT_SESSION_WIDTH = 600;

export const DEFAULT_TERMINAL_HEIGHT = 280;

export type AvatarColorKey = (typeof AVATAR_COLOR_KEYS)[number];

export type SessionTabs = {
  active?: string;
  all: string[];
};

export type SessionView = {
  scroll: Record<string, SessionScroll>;
  reviewOpen?: string[];
  pendingMessage?: string;
  pendingMessageAt?: number;
  followupCollapsed?: boolean;
  terminalOpened?: boolean;
};

export type TabHandoff = {
  dir: string;
  id: string;
  at: number;
};

export type PathHelpers = ReturnType<typeof createPathHelpers>;

export type LocalProject = Partial<Project> & {
  id: string;
  directory: string;
  expanded?: boolean;
  /** The project the workspace's machine puts it in when the account has not put it in another. */
  defaultProject?: string;
};

export type ReviewDiffStyle = "unified" | "split";

export function ensureSessionKey(
  key: string,
  touch: (key: string) => void,
  seed: (key: string) => void,
) {
  touch(key);
  seed(key);
  return key;
}

/**
 * The layout key of a workspace, or of one chat in it. Without a workspace
 * there is no scope: the empty key reads as empty and accepts no writes.
 * A folder outside every workspace (the machine's root explorer) is keyed by
 * its absolute path.
 */
export function layoutScopeKey(workspaceID: string | undefined, sessionID?: string) {
  if (!workspaceID) return "";
  return sessionID ? `${workspaceID}/${sessionID}` : workspaceID;
}

/** The inverse of `layoutScopeKey`. */
export function parseLayoutScopeKey(key: string): { workspaceID: string; sessionID?: string } | undefined {
  if (!key) return undefined;
  if (isOutsideWorkspacePath(key)) return { workspaceID: key };
  const separator = key.indexOf("/");
  if (separator === -1) return { workspaceID: key };
  return { workspaceID: key.slice(0, separator), sessionID: key.slice(separator + 1) || undefined };
}

export function createSessionKeyReader(
  sessionKey: string | Accessor<string>,
  ensure: (key: string) => void,
) {
  const key = typeof sessionKey === "function" ? sessionKey : () => sessionKey;
  return () => {
    const value = key();
    if (!value) return "";
    ensure(value);
    return value;
  };
}

export function pruneSessionKeys(input: {
  keep?: string;
  max: number;
  used: Map<string, number>;
  view: string[];
  tabs: string[];
}) {
  if (!input.keep) return [];

  const keys = new Set<string>([...input.view, ...input.tabs]);
  if (keys.size <= input.max) return [];

  const score = (key: string) => {
    if (key === input.keep) return Number.MAX_SAFE_INTEGER;
    return input.used.get(key) ?? 0;
  };

  return Array.from(keys)
    .sort((a, b) => score(b) - score(a))
    .slice(input.max);
}

export function nextSessionTabsForOpen(
  current: SessionTabs | undefined,
  tab: string,
): SessionTabs {
  const all = current?.all ?? [];
  if (!all.includes(tab)) return { all: [...all, tab], active: tab };
  return { all, active: tab };
}

export const normalizeSessionTab = (
  path: PathHelpers | undefined,
  tab: unknown,
) => {
  if (typeof tab !== "string") return undefined;
  if (!tab.startsWith("file://")) return tab;
  if (!path) return tab;

  try {
    return path.tab(tab);
  } catch {
    return undefined;
  }
};

export const normalizeSessionTabList = (
  path: PathHelpers | undefined,
  all: readonly unknown[],
) => {
  const seen = new Set<string>();
  return all.flatMap((tab) => {
    const value = normalizeSessionTab(path, tab);
    if (!value) return [];
    if (seen.has(value)) return [];
    seen.add(value);
    return [value];
  });
};
